import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ModalShell } from '../ModalShell/ModalShell';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { CtaButton } from '../CtaButton/CtaButton';
import type { CtaButtonColorVariant } from '../CtaButton/CtaButton';
import { ArrowLink } from '../ArrowLink/ArrowLink';
import { Image } from '../Image/Image';
import { StoreBadgeRow, STORE_BADGE_NAMES } from '../StoreBadgeLink/StoreBadgeLink';
import { normalizePopupId, onPopupEvent } from '../../utils/popupEvents';
import { inlineMarkdownRehypePlugins } from '../../utils/inlineMarkdown';
import { isNavigableUrl } from '../../utils/safeUrl';
import type { CtaAction } from '../../utils/ctaAction';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import './Popup.scss';

export type PopupNestedAction = Exclude<CtaAction, 'popup'>;

export type PopupHeadingSize = 'large' | 'medium' | 'small';

export type PopupColumnStyle = 'plain' | 'numbered' | 'checklist';

export type PopupMediaStyle = 'qr' | 'photo';

export const POPUP_AUTOMATIC_TRIGGERS = ['none', 'pageLoad', 'timeDelay', 'scrollDepth'] as const;
export type PopupAutomaticTrigger = (typeof POPUP_AUTOMATIC_TRIGGERS)[number];

export const POPUP_TIME_DELAY_PRESETS = ['5', '10', '15', '30'] as const;
/** Supported automatic Popup delay values, in seconds. */
export type PopupTimeDelaySeconds = (typeof POPUP_TIME_DELAY_PRESETS)[number];

export const POPUP_SCROLL_DEPTH_PRESETS = ['25', '50', '75'] as const;
/** Supported automatic Popup scroll-depth percentages. */
export type PopupScrollDepthPercent = (typeof POPUP_SCROLL_DEPTH_PRESETS)[number];

/** Prefix of the sessionStorage key that records an automatic Popup was already shown this session. */
export const POPUP_SEEN_STORAGE_PREFIX = 'poly-popup-seen:';

/**
 * @polySemanticUsage About this block: a modal popup that opens over the page, used for campaign
 * announcements, region or registration prompts, app downloads and short "how it works" explainers.
 * It renders nothing on the page itself until it opens. Give each Popup a unique Popup ID, then
 * set an eligible CTA button or ArrowLink to Action: Popup with the same Target Popup ID. Global
 * elements can reuse one target ID while each document supplies its own matching Popup. A Popup
 * may also open automatically on page load, after a time delay, or at a scroll depth; only the
 * first automatically triggered Popup on a rendered page is eligible to open. Popup content cannot
 * open another Popup. Content: optional eyebrow, a heading, an optional subheading and body text,
 * up to three columns, optional media, store badges, an arrow link and up to two buttons. Use
 * another block, not a Popup, for content every visitor must see.
 */
export interface PopupProps {
  /**
   * ID used by CTA buttons and ArrowLinks whose Action is Popup. Must be unique among Popups on
   * the rendered page. Letters, numbers and dashes; spaces become dashes and other characters are removed.
   * @polyHelp Match this value to the Target Popup ID on a CTA button or ArrowLink. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive. It must be unique among Popups on the rendered page.
   * @polyTranslatable false
   */
  popupId?: string;

  /**
   * Optional automatic trigger. Only the first Popup with an automatic trigger on the rendered page
   * can open automatically. Button and ArrowLink triggers continue to work on every Popup.
   * @polyType SelectList
   * @polyOptions None=none,Page load=pageLoad,Time delay=timeDelay,Scroll depth=scrollDepth
   * @polyDefault none
   * @polyHelp Choose whether this Popup opens automatically. Only one Popup per rendered page can use an automatic trigger. It shows at most once per browser session on each page; opening it from a CTA also counts as shown.
   * @polyTranslatable false
   */
  automaticTrigger?: PopupAutomaticTrigger;

  /**
   * Preset seconds to wait when Automatic Trigger is Time delay.
   * @polyType SelectList
   * @polyOptions 5 seconds=5,10 seconds=10,15 seconds=15,30 seconds=30
   * @polyDefault 10
   * @polyLabel Time Delay
   * @polyHelp Used only for Time delay. Choose how long to wait before opening the Popup.
   * @polyTranslatable false
   */
  automaticTriggerDelaySeconds?: PopupTimeDelaySeconds;

  /**
   * Preset percentage of the complete rendered page reached when Automatic Trigger is Scroll depth.
   * @polyType SelectList
   * @polyOptions 25%=25,50%=50,75%=75
   * @polyDefault 50
   * @polyLabel Scroll Depth
   * @polyHelp Used only for Scroll depth. Choose how far through the complete rendered page a visitor must scroll before opening the Popup.
   * @polyTranslatable false
   */
  automaticTriggerScrollDepthPercent?: PopupScrollDepthPercent;

  /**
   * Heading scale on desktop: large (72px), medium (52px) or small (38px).
   * @polyType SelectList
   * @polyOptions Large=large,Medium=medium,Small=small
   * @polyDefault medium
   * @polyTranslatable false
   */
  headingSize?: PopupHeadingSize;

  /** Short label above the heading */
  eyebrow?: string;

  /**
   * Heading; also names the dialog for screen readers. A popup needs a heading, eyebrow or
   * subheading to open.
   * @polyHelp Names the popup for screen readers. The popup only opens when it has a Heading, an Eyebrow or a Subheading.
   */
  heading?: string;

  /** Large supporting text under the heading; inline links allowed */
  subheading?: string;

  /** Regular-size paragraph under the heading; inline links allowed */
  bodyText?: string;

  /**
   * How the columns render: plain text, numbered steps, or a check list.
   * @polyType SelectList
   * @polyOptions Plain=plain,Numbered steps=numbered,Check list=checklist
   * @polyDefault plain
   * @polyTranslatable false
   */
  columnStyle?: PopupColumnStyle;

  /** Column 1 heading */
  column1Heading?: string;
  /**
   * Column 1 body as markdown: paragraphs, lists and links.
   * @polyType RichText
   */
  column1Text?: string;
  /** Arrow link at the bottom of column 1; renders only when its URL is also set */
  column1LinkText?: string;
  /**
   * Column 1 link URL
   * @polyType Url
   * @polyLabel Column 1 Link URL
   */
  column1LinkHref?: string;
  /**
   * What the column 1 link does: navigate, play a video in a player, or download a file
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download
   * @polyDefault link
   * @polyTranslatable false
   */
  column1LinkAction?: PopupNestedAction;

  /** Column 2 heading */
  column2Heading?: string;
  /**
   * Column 2 body as markdown: paragraphs, lists and links.
   * @polyType RichText
   */
  column2Text?: string;
  /** Arrow link at the bottom of column 2; renders only when its URL is also set */
  column2LinkText?: string;
  /**
   * Column 2 link URL
   * @polyType Url
   * @polyLabel Column 2 Link URL
   */
  column2LinkHref?: string;
  /**
   * What the column 2 link does: navigate, play a video in a player, or download a file
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download
   * @polyDefault link
   * @polyTranslatable false
   */
  column2LinkAction?: PopupNestedAction;

  /** Column 3 heading */
  column3Heading?: string;
  /**
   * Column 3 body as markdown: paragraphs, lists and links.
   * @polyType RichText
   */
  column3Text?: string;
  /** Arrow link at the bottom of column 3; renders only when its URL is also set */
  column3LinkText?: string;
  /**
   * Column 3 link URL
   * @polyType Url
   * @polyLabel Column 3 Link URL
   */
  column3LinkHref?: string;
  /**
   * What the column 3 link does: navigate, play a video in a player, or download a file
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download
   * @polyDefault link
   * @polyTranslatable false
   */
  column3LinkAction?: PopupNestedAction;

  /**
   * Image shown beside the text, or below it on tablet and mobile
   * @polyType Url
   */
  imageUrl?: string;

  /**
   * Image alt text. Leave empty for a decorative image or a QR code that duplicates a labelled link.
   * @polyLabel Image Alt Text
   */
  imageAlt?: string;

  /**
   * QR code shows the image at a fixed, scannable size on a white card; Photo fills the media column.
   * @polyType SelectList
   * @polyOptions QR code=qr,Photo=photo
   * @polyDefault photo
   * @polyTranslatable false
   */
  imageStyle?: PopupMediaStyle;

  /** Shows the App Store and Google Play badges */
  showStoreBadges?: boolean;

  /**
   * ID prefix for the store badge links; each badge is suffixed with its store name
   * @polyTranslatable false
   */
  storeBadgesId?: string;

  /**
   * data-ld-append value applied to both store badge links
   * @polyTranslatable false
   */
  storeBadgesLdAppendix?: string;

  /** Arrow link under the text; renders only when its URL is also set */
  linkText?: string;
  /**
   * Arrow link URL
   * @polyType Url
   * @polyLabel Link URL
   */
  linkHref?: string;
  /**
   * What the arrow link does: navigate, play a video in a player, or download a file
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download
   * @polyDefault link
   * @polyTranslatable false
   */
  linkAction?: PopupNestedAction;

  /** Primary button text; the button renders only when its URL is also set */
  primaryCtaText?: string;
  /**
   * Primary button URL
   * @polyType Url
   * @polyLabel Primary CTA URL
   */
  primaryCtaHref?: string;
  /**
   * Primary button color: Smile Orange for registration actions (Sign up, Start selling), dark otherwise
   * @polyType SelectList
   * @polyOptions Orange=primary,Dark=contrast
   * @polyDefault contrast
   * @polyLabel Primary CTA Color
   * @polyTranslatable false
   */
  primaryCtaColorVariant?: Extract<CtaButtonColorVariant, 'primary' | 'contrast'>;
  /**
   * Primary button id for analytics
   * @polyTranslatable false
   */
  primaryCtaId?: string;
  /**
   * data-ld-append value for the primary button
   * @polyTranslatable false
   */
  primaryCtaLdAppendix?: string;
  /**
   * Whether the primary button opens in the current tab or a new one
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  primaryCtaBehavior?: CtaBehavior;

  /** Secondary (outlined) button text; renders only when its URL is also set */
  secondaryCtaText?: string;
  /**
   * Secondary button URL
   * @polyType Url
   * @polyLabel Secondary CTA URL
   */
  secondaryCtaHref?: string;
  /**
   * Secondary button id for analytics
   * @polyTranslatable false
   */
  secondaryCtaId?: string;
  /**
   * data-ld-append value for the secondary button
   * @polyTranslatable false
   */
  secondaryCtaLdAppendix?: string;
  /**
   * Whether the secondary button opens in the current tab or a new one
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyDefault sameTab
   * @polyTranslatable false
   */
  secondaryCtaBehavior?: CtaBehavior;

  /**
   * Short line under the buttons (an incentive or terms note); inline links allowed
   * @polyLabel Text Below CTAs
   */
  ctaSubtext?: string;

  /**
   * Screen-reader name of the close button
   * @polyDefault Close
   */
  closeButtonAriaLabel?: string;
}

interface PopupColumn {
  heading?: string;
  text?: string;
  linkText?: string;
  linkHref?: string;
  linkAction?: PopupNestedAction;
}

/**
 * Authored markdown links go through the same href check as the block's own links. A link whose
 * URL is not navigable (javascript:, data:, protocol-relative) keeps its text and drops the anchor.
 */
const SafeMarkdownLink: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement>> = ({ href, children }) =>
  isNavigableUrl(href) ? <a href={href}>{children}</a> : <>{children}</>;

const MARKDOWN_COMPONENTS = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <SafeMarkdownLink href={href}>{children}</SafeMarkdownLink>
  ),
};

const INLINE_MARKDOWN_COMPONENTS = {
  ...MARKDOWN_COMPONENTS,
  p: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
};

const InlineMarkdown: React.FC<{ children: string }> = ({ children }) => (
  <ReactMarkdown rehypePlugins={inlineMarkdownRehypePlugins} components={INLINE_MARKDOWN_COMPONENTS}>
    {children}
  </ReactMarkdown>
);

const hasSeenThisSession = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const markSeenThisSession = (key: string): void => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Storage can be blocked (private mode, disabled cookies); the popup then shows on every load.
  }
};

const getSeenStorageKey = (popupId: string, fallbackKey: string): string =>
  `${POPUP_SEEN_STORAGE_PREFIX}${window.location.pathname}#${popupId || fallbackKey}`;

const isFirstMatchingPopup = (root: HTMLDivElement | null, popupId: string): boolean =>
  !popupId || document.querySelector(`[data-poly-popup-id="${popupId}"]`) === root;

const isFirstAutomaticPopup = (root: HTMLDivElement | null): boolean =>
  document.querySelector('[data-poly-popup-automatic="true"]') === root;

/**
 * Open state for targeted CTA events and the configured automatic trigger. Duplicate Popup IDs
 * only open the first matching block, and only the first automatic Popup on a page can open itself.
 */
const usePopupTriggers = (
  popupId: string,
  automaticTrigger: PopupAutomaticTrigger,
  delaySeconds: PopupTimeDelaySeconds,
  scrollDepthPercent: PopupScrollDepthPercent,
  fallbackKey: string,
  canOpen: boolean,
  rootRef: React.RefObject<HTMLDivElement | null>,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  const openFrom = useCallback((trigger: HTMLElement | null) => {
    if (isOpenRef.current) return;
    isOpenRef.current = true;
    triggerRef.current = trigger;
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!popupId || !canOpen) return undefined;

    return onPopupEvent((requestedId, trigger) => {
      if (
        normalizePopupId(requestedId) === popupId &&
        isFirstMatchingPopup(rootRef.current, popupId)
      ) {
        if (automaticTrigger !== 'none') {
          markSeenThisSession(
            getSeenStorageKey(popupId, fallbackKey),
          );
        }
        const focusTarget = trigger?.matches('button, a[href], [role="button"], [tabindex]')
          ? trigger
          : null;
        openFrom(focusTarget);
      }
    });
  }, [automaticTrigger, canOpen, fallbackKey, popupId, openFrom, rootRef]);

  useEffect(() => {
    if (!canOpen || automaticTrigger === 'none') return undefined;

    const storageKey = getSeenStorageKey(popupId, fallbackKey);
    const openAutomatically = () => {
      if (
        !isFirstAutomaticPopup(rootRef.current) ||
        !isFirstMatchingPopup(rootRef.current, popupId) ||
        hasSeenThisSession(storageKey)
      ) return;
      markSeenThisSession(storageKey);
      openFrom(null);
    };

    if (automaticTrigger === 'pageLoad') {
      openAutomatically();
      return undefined;
    }

    if (automaticTrigger === 'timeDelay') {
      const parsedDelaySeconds = Number(delaySeconds);
      const safeDelaySeconds = Number.isFinite(parsedDelaySeconds)
        ? Math.max(0, parsedDelaySeconds)
        : 10;
      const timeoutId = window.setTimeout(openAutomatically, safeDelaySeconds * 1000);
      return () => window.clearTimeout(timeoutId);
    }

    const parsedScrollDepth = Number(scrollDepthPercent);
    const safeScrollDepth = Number.isFinite(parsedScrollDepth)
      ? Math.min(100, Math.max(1, parsedScrollDepth))
      : 50;
    const handleScroll = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll > 0 && (window.scrollY / maxScroll) * 100 < safeScrollDepth) return;
      window.removeEventListener('scroll', handleScroll);
      openAutomatically();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [
    automaticTrigger,
    canOpen,
    delaySeconds,
    fallbackKey,
    openFrom,
    popupId,
    rootRef,
    scrollDepthPercent,
  ]);

  return { isOpen, close, triggerRef };
};

export const Popup: React.FC<PopupProps> = ({
  popupId,
  automaticTrigger = 'none',
  automaticTriggerDelaySeconds = '10',
  automaticTriggerScrollDepthPercent = '50',
  headingSize = 'medium',
  eyebrow,
  heading,
  subheading,
  bodyText,
  columnStyle = 'plain',
  column1Heading,
  column1Text,
  column1LinkText,
  column1LinkHref,
  column1LinkAction,
  column2Heading,
  column2Text,
  column2LinkText,
  column2LinkHref,
  column2LinkAction,
  column3Heading,
  column3Text,
  column3LinkText,
  column3LinkHref,
  column3LinkAction,
  imageUrl,
  imageAlt = '',
  imageStyle = 'photo',
  showStoreBadges = false,
  storeBadgesId,
  storeBadgesLdAppendix,
  linkText,
  linkHref,
  linkAction,
  primaryCtaText,
  primaryCtaHref,
  primaryCtaColorVariant = 'contrast',
  primaryCtaId,
  primaryCtaLdAppendix,
  primaryCtaBehavior,
  secondaryCtaText,
  secondaryCtaHref,
  secondaryCtaId,
  secondaryCtaLdAppendix,
  secondaryCtaBehavior,
  ctaSubtext,
  closeButtonAriaLabel = 'Close',
}) => {
  const id = normalizePopupId(popupId);
  const rootRef = useRef<HTMLDivElement>(null);
  const [isDuplicateId, setIsDuplicateId] = useState(false);
  const [isAdditionalAutomaticPopup, setIsAdditionalAutomaticPopup] = useState(false);
  const headingId = useId();
  const nameId = useId();
  // The dialog is named by its heading, else the first visible text that can stand in for one.
  // A fixed fallback name would be the same English word on every locale, so a popup with none of
  // these is a config error: it is flagged and never opens, rather than opening unnamed.
  const nameSource = heading ? 'heading' : eyebrow ? 'eyebrow' : subheading ? 'subheading' : undefined;
  const nameText = nameSource ? { heading, eyebrow, subheading }[nameSource] ?? '' : '';
  const hasNoName = !nameSource;
  const cannotOpen = !id && automaticTrigger === 'none';
  const { isOpen, close, triggerRef } = usePopupTriggers(
    id,
    automaticTrigger,
    automaticTriggerDelaySeconds,
    automaticTriggerScrollDepthPercent,
    nameText,
    !hasNoName,
    rootRef,
  );

  useEffect(() => {
    const duplicate = Boolean(id && !isFirstMatchingPopup(rootRef.current, id));
    setIsDuplicateId(duplicate);
    if (duplicate && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- surface duplicate authored Popup IDs during development
      console.warn(`[Popup] Popup ID "${id}" is duplicated. Only the first matching Popup can open.`);
    }
  }, [id]);

  useEffect(() => {
    const additionalAutomatic = automaticTrigger !== 'none' && !hasNoName && !isFirstAutomaticPopup(rootRef.current);
    setIsAdditionalAutomaticPopup(additionalAutomatic);
    if (additionalAutomatic && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- surface competing automatic Popup configuration
      console.warn('[Popup] Only the first Popup with an Automatic Trigger can open automatically on a page.');
    }
  }, [automaticTrigger, hasNoName]);

  useEffect(() => {
    if (cannotOpen && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- surface CMS mis-config to authors during dev
      console.warn('[Popup] Set a Popup ID or choose an Automatic Trigger; otherwise nothing can open this Popup.');
    }
  }, [cannotOpen]);

  useEffect(() => {
    if (hasNoName && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- surface CMS mis-config to authors during dev
      console.warn('[Popup] Set a Heading (or an Eyebrow or Subheading); screen readers need it to name the popup.');
    }
  }, [hasNoName]);

  const columns: PopupColumn[] = [
    { heading: column1Heading, text: column1Text, linkText: column1LinkText, linkHref: column1LinkHref, linkAction: column1LinkAction },
    { heading: column2Heading, text: column2Text, linkText: column2LinkText, linkHref: column2LinkHref, linkAction: column2LinkAction },
    { heading: column3Heading, text: column3Text, linkText: column3LinkText, linkHref: column3LinkHref, linkAction: column3LinkAction },
  ].filter((column) => column.heading || column.text || (column.linkText && isNavigableUrl(column.linkHref)));

  const showLink = Boolean(linkText && isNavigableUrl(linkHref));
  const showPrimaryCta = Boolean(primaryCtaText && isNavigableUrl(primaryCtaHref));
  const showSecondaryCta = Boolean(secondaryCtaText && isNavigableUrl(secondaryCtaHref));
  const hasImage = Boolean(imageUrl);
  const ColumnsTag = columnStyle === 'numbered' ? 'ol' : 'ul';

  const renderColumnMarker = (index: number) => {
    if (columnStyle === 'numbered') {
      return (
        <span className="poly-popup__number" aria-hidden="true">
          {index + 1}
        </span>
      );
    }
    return columnStyle === 'checklist' ? (
      <CustomIcon
        name="check"
        size="var(--poly-size-popup-checkIcon)"
        color="var(--poly-color-popup-checkIcon)"
        className="poly-popup__check-icon"
        aria-hidden="true"
      />
    ) : null;
  };

  return (
    <div
      ref={rootRef}
      className="poly-popup"
      data-poly-block="Popup"
      data-poly-popup-id={id || undefined}
      data-poly-popup-automatic={automaticTrigger !== 'none' && !hasNoName ? 'true' : undefined}
      data-poly-error={cannotOpen
        ? 'popup-cannot-open'
        : hasNoName
          ? 'popup-missing-heading'
          : isDuplicateId
            ? 'popup-duplicate-id'
            : isAdditionalAutomaticPopup
              ? 'popup-multiple-automatic'
              : undefined}
    >
      {isOpen && !hasNoName && (
        <ModalShell
          onClose={close}
          labelledBy={nameSource === 'heading' ? headingId : nameSource ? nameId : undefined}
          ariaLabel={nameText}
          closeLabel={closeButtonAriaLabel}
          returnFocusRef={triggerRef}
          className={`poly-popup__panel poly-popup--heading-${headingSize}`}
        >
          <div className={`poly-popup__layout${hasImage ? ' poly-popup__layout--with-media' : ''}`}>
            <div className="poly-popup__content">
              {(eyebrow || heading || subheading || bodyText) && (
                <div className="poly-popup__header">
                  {eyebrow && (
                    <span id={nameSource === 'eyebrow' ? nameId : undefined} className="poly-popup__eyebrow">
                      {eyebrow}
                    </span>
                  )}
                  {heading && (
                    <h2 id={headingId} className="poly-popup__heading">
                      {heading}
                    </h2>
                  )}
                  {subheading && (
                    <p id={nameSource === 'subheading' ? nameId : undefined} className="poly-popup__subheading">
                      <InlineMarkdown>{subheading}</InlineMarkdown>
                    </p>
                  )}
                  {bodyText && (
                    <p className="poly-popup__body-text">
                      <InlineMarkdown>{bodyText}</InlineMarkdown>
                    </p>
                  )}
                </div>
              )}

              {showLink && (
                <ArrowLink className="poly-popup__link" href={linkHref} action={linkAction} direction="right">
                  {linkText}
                </ArrowLink>
              )}

              {showStoreBadges && (
                <StoreBadgeRow
                  stores={STORE_BADGE_NAMES}
                  id={storeBadgesId}
                  ldAppendix={storeBadgesLdAppendix}
                  className="poly-popup__badges"
                />
              )}

              {columns.length > 0 && (
                <ColumnsTag
                  // list-style: none hides the markers, and Safari VoiceOver then stops announcing the list.
                  role="list"
                  className={`poly-popup__columns poly-popup__columns--${columnStyle}`}
                >
                  {columns.map((column, index) => (
                    <li key={index} className="poly-popup__column">
                      {renderColumnMarker(index)}
                      <div className="poly-popup__column-content">
                        {column.heading && <h3 className="poly-popup__column-heading">{column.heading}</h3>}
                        {column.text && (
                          <div className="poly-popup__column-text">
                            <ReactMarkdown components={MARKDOWN_COMPONENTS}>{column.text}</ReactMarkdown>
                          </div>
                        )}
                        {column.linkText && isNavigableUrl(column.linkHref) && (
                          <ArrowLink
                            className="poly-popup__column-link"
                            href={column.linkHref}
                            action={column.linkAction}
                            direction="right"
                          >
                            {column.linkText}
                          </ArrowLink>
                        )}
                      </div>
                    </li>
                  ))}
                </ColumnsTag>
              )}

              {(showPrimaryCta || showSecondaryCta || ctaSubtext) && (
                <div className="poly-popup__actions">
                  {(showPrimaryCta || showSecondaryCta) && (
                    <div className="poly-popup__buttons">
                      {showPrimaryCta && (
                        <CtaButton
                          text={primaryCtaText}
                          href={primaryCtaHref}
                          colorVariant={primaryCtaColorVariant}
                          size="small"
                          id={primaryCtaId}
                          ldAppendix={primaryCtaLdAppendix}
                          behavior={primaryCtaBehavior}
                        />
                      )}
                      {showSecondaryCta && (
                        <CtaButton
                          text={secondaryCtaText}
                          href={secondaryCtaHref}
                          colorVariant="secondary"
                          size="small"
                          id={secondaryCtaId}
                          ldAppendix={secondaryCtaLdAppendix}
                          behavior={secondaryCtaBehavior}
                        />
                      )}
                    </div>
                  )}
                  {ctaSubtext && (
                    <p className="poly-popup__cta-subtext">
                      <InlineMarkdown>{ctaSubtext}</InlineMarkdown>
                    </p>
                  )}
                </div>
              )}
            </div>

            {hasImage && (
              <div className={`poly-popup__media poly-popup__media--${imageStyle}`}>
                <Image bare url={imageUrl as string} alt={imageAlt} className="poly-popup__image" />
              </div>
            )}
          </div>
        </ModalShell>
      )}
    </div>
  );
};
